/**
 * API client with authentication and error handling.
 * Uses environment variables for base URL - NO hardcoded values.
 */
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';
import { config } from '../config';

// Create axios instance with base URL from environment
const apiClient: AxiosInstance = axios.create({
  baseURL: config.apiBaseUrl,  // From environment variable ONLY
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: Add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token');

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: Handle token refresh and errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized - try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');

        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Refresh token
        const response = await axios.post(
          `${config.apiBaseUrl}/api/auth/refresh`,
          { refresh_token: refreshToken }
        );

        const { access_token, refresh_token: newRefreshToken } = response.data;

        // Store new tokens
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', newRefreshToken);

        // Retry original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
        }

        return apiClient(originalRequest);

      } catch (refreshError) {
        // Refresh failed - logout user
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');

        toast.error('Session expired. Please log in again.');

        // Delay redirect slightly to show toast
        setTimeout(() => {
          window.location.href = '/login';
        }, 1000);

        return Promise.reject(refreshError);
      }
    }

    const errorDetail = (error.response?.data as { detail?: { error_code?: string; message?: string } } | undefined)?.detail;

    // Handle 402 Payment Required (subscription lapsed) — keep the session, send the user to billing
    if (error.response?.status === 402) {
      toast.error(errorDetail?.message || 'Your subscription has expired. Please renew to continue.', {
        id: 'subscription-required',
        duration: 8000,
      });
      window.dispatchEvent(new CustomEvent('navigate-view', { detail: 'subscription' }));
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      if (errorDetail?.error_code === 'GRACE_PERIOD_RESTRICTED') {
        toast.error(errorDetail.message || 'This action is unavailable during your grace period.', {
          id: 'grace-period-restricted',
          duration: 8000,
        });
      } else {
        toast.error('You do not have permission to perform this action.');
      }
    }

    // Handle 429 Rate Limit
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'];
      const message = retryAfter
        ? `Too many requests. Please try again in ${retryAfter} seconds.`
        : 'Too many requests. Please slow down and try again later.';

      toast.error(message, { duration: 6000 });
    }

    // Handle 500+ Server Errors
    if (error.response?.status && error.response.status >= 500) {
      // Don't show toast for all 500 errors - let components handle specific cases
      // But log to console for debugging
      console.error('Server error:', error.response.status, error.response.data);
    }

    // Handle Network Errors
    if (error.message === 'Network Error' || !error.response) {
      toast.error('Network error. Please check your internet connection.', {
        duration: 5000,
      });
    }

    // Handle Timeout
    if (error.code === 'ECONNABORTED') {
      toast.error('Request timeout. Please try again.', {
        duration: 4000,
      });
    }

    return Promise.reject(error);
  }
);

export default apiClient;
